/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateXAtomValue,
  useStateXSelector,
  useStateXValue,
} from '@cloudio-saas/statex';
import { aliasAtom } from 'components/DataStore/state';
import { VirtualizedSelectInput } from 'custom/SelectInput';
import { helpTextSelector } from 'designer/state';
import React, { memo, useMemo } from 'react';
import {
  DataSourceAliasChooserProp,
  DSType,
  getName,
  Item,
  Platform,
} from 'componentregistry';
import { designPath } from 'designer/atoms';
import { isEmpty } from 'utils/isEmpty';
import { usePlatform } from 'platform/PlatformHooks';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

export function getAllDataSources(
  platform: Platform,
  appUid: string,
  pageId: string,
): Item<DSType>[] {
  const page = platform.getPage(appUid, pageId);
  if (!page) {
    return [];
  }
  const itemIds = Object.keys(page);
  return itemIds
    .filter(
      (id) =>
        page[id]?.type === 'CloudDataStore' ||
        page[id]?.type === 'DataStore' ||
        page[id]?.type === 'ReadOnlyDataStore' ||
        page[id]?.type === 'LocalDataStore' ||
        page[id]?.type === 'TransientDataStore',
    )
    .map((id) => page[id] as Item<DSType>);
}

export function DataSourceAliasChooser({
  appUid,
  pageId,
  value,
  onChange,
  required = false,
  label,
  helpText,
  testid,
  allowedDSTypes,
  includeSystem,
  isViewOnlyUser = false,
}: {
  appUid: string;
  pageId: string;
  value: string;
  required?: boolean;
  label: string;
  helpText?: string;
  testid?: string;
  allowedDSTypes: DSType[];
  onChange: (val: string) => void;
  includeSystem?: boolean;
  isViewOnlyUser?: boolean;
}) {
  const aliases = useStateXAtomValue(aliasAtom, {
    params: { appUid, pageId },
  });
  const platform = usePlatform();
  helpText =
    helpText ||
    (allowedDSTypes.length < 5
      ? `Allowed types: ${allowedDSTypes.map((t) => getName(t)).join(', ')}`
      : undefined);

  const options = useMemo(() => {
    const items = ['$$Sidebar$$'].includes(pageId)
      ? []
      : getAllDataSources(platform, appUid, pageId);
    const _options = ['$$Sidebar$$'].includes(pageId)
      ? Object.keys(aliases).map((alias) => ({ value: alias }))
      : Object.keys(aliases)
          .filter((alias) => {
            const ds = items.find((item) => item.props.alias === alias);
            return ds && allowedDSTypes.includes(ds.type);
          })
          .map((alias) => ({ value: alias }));

    return _options.sort((a, b) => a.value.localeCompare(b.value));
  }, [aliases, allowedDSTypes, appUid, pageId, platform]);

  return (
    <VirtualizedSelectInput
      disabled={isViewOnlyUser}
      data-testid={testid}
      value={value ?? ''}
      options={options}
      onChange={onChange}
      disableClearable={false} // allow clearing the value even if required
      label={label}
      variant="standard"
      required={required}
      helpText={helpText}
    />
  );
}

function DataSourceAliasChooserProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<DataSourceAliasChooserProp, string>) {
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const handleChange = (alias: string) => {
    if (alias !== value) {
      onChange(property, alias);
    }
  };
  let aliasPageId = useStateXValue(
    designPath({
      appUid,
      pageId,
      itemId: activeItemId,
      key: 'props',
      subkey: def.pageIdProp ?? '_no_embedded_page_id_',
    }),
    '',
  );
  if (isEmpty(aliasPageId)) {
    aliasPageId = pageId;
  }

  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  return (
    <DataSourceAliasChooser
      isViewOnlyUser={isViewOnlyUser}
      appUid={appUid}
      helpText={helpText}
      label={def.label}
      onChange={handleChange}
      pageId={aliasPageId}
      required={required}
      testid={property}
      value={value ?? ''}
      allowedDSTypes={def.allowedTypes}
    />
  );
}

export default memo(DataSourceAliasChooserProperty);
